import type { LocalDate, MeteoDaily, MeteoDayResponse, MeteoWeekResponse } from '@ergo/shared';
import { useMemo } from 'react';
import { api } from './api';
import { useResource } from './hooks';
import { isValidDate } from './time';

/** Ce que l'app affiche quand elle ne sait rien : rien du tout. */
const NO_DAY: MeteoDayResponse = { available: false, fetchedAt: null, hours: [], daily: null };
const NO_WEEK: MeteoWeekResponse = { available: false, fetchedAt: null, days: [] };

/**
 * Le temps qu'il fera ce jour-là. Une erreur ne remonte pas : comme les vacances
 * scolaires et l'agenda perso, c'est un décor utile — jamais une raison d'abîmer
 * l'écran. Réglages est le seul endroit qui dit qu'une récupération a échoué.
 */
export function useMeteoDay(date: LocalDate): MeteoDayResponse {
  const resource = useResource(
    () => (isValidDate(date) ? api.meteo.day(date).catch(() => NO_DAY) : Promise.resolve(NO_DAY)),
    [date],
  );
  return resource.data ?? NO_DAY;
}

/** Les résumés des sept jours d'une semaine, par jour ; vide hors prévision. */
export function useMeteoWeek(from: LocalDate): Map<LocalDate, MeteoDaily> {
  const resource = useResource(
    () => (isValidDate(from) ? api.meteo.week(from).catch(() => NO_WEEK) : Promise.resolve(NO_WEEK)),
    [from],
  );
  const days = resource.data?.days;

  return useMemo(
    () => new Map((days ?? []).map((day) => [day.date, day.daily])),
    [days],
  );
}
